import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDaysIcon, ClockIcon, GiftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  eventIsAllDay,
  eventStartKey,
  eventEndInclusiveKey,
} from '../viewServices/calendar_grid_utils';
import {
  CALENDAR_TYPE_BIRTHDAY,
  CALENDAR_TYPE_TASK,
  plannerCalendarNormalizedType,
} from '../../moduleServices/planner_calendar_types';
import { formatPlannerEventHm, PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';

const RADIUS_MD = '0.375rem';

const SHOW_HOVER_CARD_MS = 120;
/** Rough max card height — used only to flip above/below viewport edge */
const CARD_ESTIMATE_PX = 140;
const TOOLTIP_MAX_W_PX = 280;
const GAP_PX = 8;
const EDGE_MARGIN_PX = 10;

/** @returns {number | undefined} epoch ms local calendar day midnight from YYYY-MM-DD */
function millisFromLocalDateKey(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey ?? '')) return undefined;
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
}

function fmtDateKeyPretty(dateKey) {
  const t = millisFromLocalDateKey(dateKey);
  if (t === undefined) return '';
  const d = new Date(t);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** @returns {{ title: string, dateText: string, timeText: string }} */
function plannerEventHoverContent(event, formatMode) {
  const title = (event.title && String(event.title).trim()) || 'Untitled';
  const sk = eventStartKey(event);
  const ek = eventEndInclusiveKey(event);

  /** @type {{ dateText: string, timeText: string }} */
  const out = { dateText: '', timeText: '' };

  const isAllday = eventIsAllDay(event);

  if (isAllday) {
    if (sk && ek && sk !== ek) {
      out.dateText = `${fmtDateKeyPretty(sk)} – ${fmtDateKeyPretty(ek)}`;
    } else if (sk) {
      out.dateText = fmtDateKeyPretty(sk);
    }
    out.timeText = out.dateText ? 'All day' : '';
    return { title, ...out };
  }

  if (!event.start_datetime) {
    out.dateText = sk ? fmtDateKeyPretty(sk) : '';
    out.timeText = 'No start time';
    return { title, ...out };
  }

  const startInstant = new Date(event.start_datetime);
  if (sk && ek && sk !== ek) {
    out.dateText = `${fmtDateKeyPretty(sk)} – ${fmtDateKeyPretty(ek)}`;
  } else {
    out.dateText =
      fmtDateKeyPretty(sk) ||
      startInstant.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
  }

  const t0 = formatPlannerEventHm(event.start_datetime, formatMode);
  const t1 = formatPlannerEventHm(event.end_datetime, formatMode);
  if (t0 && t1 && t0 !== t1) out.timeText = `${t0} – ${t1}`;
  else if (t0) out.timeText = t0;

  return { title, ...out };
}

function HoverMetaRow({ icon: Icon, children }) {
  return (
    <div className="flex gap-1.5 items-start text-[11px] leading-snug text-gray-500">
      <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400 mt-px" aria-hidden />
      <span className="min-w-0 flex-1 tabular-nums break-words">{children}</span>
    </div>
  );
}

function EventHoverPortal({ event, style, formatMode }) {
  const { title, dateText, timeText } = plannerEventHoverContent(event, formatMode);
  const calendarName = (event.calendar?.name && String(event.calendar.name).trim()) || '';
  const calendarColor = event.calendar?.color || '#94a3b8';

  return createPortal(
    <div className="pointer-events-none fixed z-[100]" role="presentation" style={style}>
      <div
        className="min-w-[min(200px,calc(100vw-40px))] max-w-[min(280px,calc(100vw-24px))] rounded-md border border-gray-200/55 bg-white px-2.5 py-2 text-left shadow-sm"
        style={{ maxWidth: TOOLTIP_MAX_W_PX }}
      >
        <p className="text-sm font-medium leading-snug text-gray-800 break-words">{title}</p>
        {calendarName ? (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-snug text-gray-600">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: calendarColor }}
              aria-hidden
            />
            <span className="min-w-0 truncate">{calendarName}</span>
          </div>
        ) : null}
        {dateText ? (
          <div className="mt-1.5">
            <HoverMetaRow icon={CalendarDaysIcon}>{dateText}</HoverMetaRow>
          </div>
        ) : null}
        {timeText ? (
          <div className={dateText ? 'mt-1' : 'mt-1.5'}>
            <HoverMetaRow icon={ClockIcon}>{timeText}</HoverMetaRow>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function computeHoverPlacement(rect) {
  const minTopClearance = EDGE_MARGIN_PX + GAP_PX + CARD_ESTIMATE_PX;
  const placeAbove = rect.top >= minTopClearance;

  let left = rect.left + rect.width / 2;
  const halfGuess = Math.min(TOOLTIP_MAX_W_PX / 2, (window.innerWidth - 2 * EDGE_MARGIN_PX) / 2);
  left = Math.min(
    window.innerWidth - EDGE_MARGIN_PX - halfGuess,
    Math.max(EDGE_MARGIN_PX + halfGuess, left),
  );

  if (placeAbove) {
    return {
      style: {
        left,
        top: rect.top - GAP_PX,
        transform: 'translate(-50%, -100%)',
      },
    };
  }
  return {
    style: {
      left,
      top: rect.bottom + GAP_PX,
      transform: 'translate(-50%, 0)',
    },
  };
}

/**
 * compactMobileMonthCell: mobile month grid — tap passes to cell (day sheet); slimmer chrome.
 *
 * @param {{
 *   event: object,
 *   onClick?: (event: object) => void,
 *   compact?: boolean,
 *   compactMobileMonthCell?: boolean,
 *   monthSegment?: {
 *     hideInlineLabel?: boolean,
 *     strongLeftAccent?: boolean,
 *     roundLeft?: boolean,
 *     roundRight?: boolean,
 *   },
 *   timeFormatPreference?: string,
 * }} props
 */
const CalendarEventItem = ({
  event,
  onClick,
  compact = false,
  compactMobileMonthCell = false,
  monthSegment,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) => {
  const triggerRef = useRef(null);
  const [interaction, setInteraction] = useState(false);
  const [debouncedVisible, setDebouncedVisible] = useState(false);
  const debounceTimerRef = useRef(null);
  const [hoverStyle, setHoverStyle] = useState(null);

  const hoverDisabled = compact && compactMobileMonthCell;
  const chipInteractive = !hoverDisabled;

  const scheduleShow = useCallback(() => {
    if (hoverDisabled) return;
    setInteraction(true);
  }, [hoverDisabled]);
  const scheduleHide = useCallback(() => setInteraction(false), []);

  useEffect(() => {
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (hoverDisabled) {
      setDebouncedVisible(false);
      setInteraction(false);
      return;
    }
    if (!interaction) {
      setDebouncedVisible(false);
      return;
    }
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      setDebouncedVisible(true);
    }, SHOW_HOVER_CARD_MS);
    return () => {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [hoverDisabled, interaction]);

  const dismissPortal = useCallback(() => scheduleHide(), [scheduleHide]);

  useLayoutEffect(() => {
    if (hoverDisabled || !debouncedVisible || !triggerRef.current) {
      setHoverStyle(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const { style } = computeHoverPlacement(rect);
    setHoverStyle(style);
    const reposition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHoverStyle(computeHoverPlacement(r).style);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', dismissPortal, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', dismissPortal, true);
    };
  }, [hoverDisabled, debouncedVisible, dismissPortal]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (typeof onClick === 'function') onClick(event);
  };

  const {
    hideInlineLabel = false,
    strongLeftAccent = true,
    roundLeft = true,
    roundRight = true,
  } = monthSegment || {};

  const calendarColor = event.calendar?.color || '#D97706';
  const plannerCalType = plannerCalendarNormalizedType(event?.calendar);
  const isBirthdayCalendar = strongLeftAccent && plannerCalType === CALENDAR_TYPE_BIRTHDAY;
  const isTaskCalendar = strongLeftAccent && plannerCalType === CALENDAR_TYPE_TASK;
  const isAllDay = eventIsAllDay(event);
  const startTime = isAllDay ? '' : formatPlannerEventHm(event.start_datetime, timeFormatPreference);

  const { title: hTitle, dateText: hDate, timeText: hTime } = plannerEventHoverContent(
    event,
    timeFormatPreference,
  );
  const accentKindLabel = isBirthdayCalendar ? 'Birthday' : isTaskCalendar ? 'Task' : null;
  const richAriaLabel = [accentKindLabel, hTitle, event.calendar?.name, hDate, hTime].filter(Boolean).join('. ');

  const softBg = `${calendarColor}20`;

  const tl = roundLeft ? RADIUS_MD : '0';
  const tr = roundRight ? RADIUS_MD : '0';
  const br = roundRight ? RADIUS_MD : '0';
  const bl = roundLeft ? RADIUS_MD : '0';

  const slimMobileMonthChip = compact && compactMobileMonthCell;
  const showInlineTime = !hideInlineLabel && startTime && !slimMobileMonthChip;
  const titleTextClass = slimMobileMonthChip
    ? 'text-[10px] leading-none'
    : compact
      ? 'text-[11px] leading-none'
      : 'text-[11px] leading-snug';

  const chipStyle = {
    backgroundColor: softBg,
    borderTopLeftRadius: tl,
    borderTopRightRadius: tr,
    borderBottomRightRadius: br,
    borderBottomLeftRadius: bl,
  };

  const chipSurfaceClass = [
    'w-full max-w-full flex min-w-0 items-stretch overflow-hidden shrink-0 text-left',
    'ring-1 ring-inset ring-stone-200/85 transition-[filter,ring-color]',
    compact ? 'h-[17px] min-h-[17px] max-h-[17px]' : 'py-[3px] min-h-[21px]',
    chipInteractive
      ? 'cursor-pointer hover:ring-stone-300/90 hover:brightness-[0.995] active:brightness-[0.98]'
      : 'pointer-events-none cursor-default',
  ].join(' ');

  const leftAccentThinBar = (
    <span
      className="flex shrink-0 self-stretch overflow-hidden w-[4px] min-w-[4px]"
      style={{
        backgroundColor: calendarColor,
        borderTopLeftRadius: tl,
        borderBottomLeftRadius: bl,
      }}
      aria-hidden="true"
    />
  );

  const accentBlock = strongLeftAccent ? (
    isBirthdayCalendar ? (
      <span
        className={`flex shrink-0 self-stretch items-center justify-center overflow-hidden ${
          compact ? 'w-[14px] min-w-[14px]' : 'w-[17px] min-w-[17px]'
        }`}
        style={{
          borderTopLeftRadius: tl,
          borderBottomLeftRadius: bl,
        }}
        aria-hidden="true"
      >
        <span
          className={`flex shrink-0 items-center justify-center rounded-full ${
            compact ? 'h-3 w-3' : 'h-[0.9375rem] w-[0.9375rem]'
          }`}
          style={{ backgroundColor: calendarColor }}
        >
          <GiftIcon
            className={`shrink-0 text-white ${compact ? 'h-2 w-2' : 'h-2.5 w-2.5'}`}
          />
        </span>
      </span>
    ) : isTaskCalendar ? (
      <span
        className={`flex shrink-0 self-stretch items-center justify-center overflow-hidden pr-px ${
          compact ? 'w-[14px] min-w-[14px]' : 'w-[17px] min-w-[17px]'
        }`}
        style={{
          borderTopLeftRadius: tl,
          borderBottomLeftRadius: bl,
        }}
        aria-hidden="true"
      >
        <CheckCircleIcon
          className={`shrink-0 ${compact ? 'h-3 w-3' : 'h-[0.9375rem] w-[0.9375rem]'}`}
          style={{ color: calendarColor }}
        />
      </span>
    ) : (
      leftAccentThinBar
    )
  ) : null;

  const labelBlock = (
    <span
      className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden leading-none ${isTaskCalendar || isBirthdayCalendar ? 'px-0.5' : 'px-1'}`}
    >
      {showInlineTime ? (
        <span className="text-[10px] font-normal tabular-nums shrink-0 text-gray-600">{startTime}</span>
      ) : null}
      {!hideInlineLabel ? (
        <span className={`min-w-0 flex-1 font-medium overflow-hidden whitespace-nowrap text-clip text-gray-900 ${titleTextClass}`}>
          {event.title?.trim?.() ? event.title : 'Untitled'}
        </span>
      ) : (
        <span className="min-w-0 flex-1" aria-hidden />
      )}
    </span>
  );

  return (
    <>
      {chipInteractive ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={handleClick}
          aria-label={richAriaLabel}
          onMouseEnter={scheduleShow}
          onMouseLeave={scheduleHide}
          onFocus={scheduleShow}
          onBlur={scheduleHide}
          style={chipStyle}
          className={chipSurfaceClass}
        >
          {accentBlock}
          {labelBlock}
        </button>
      ) : (
        <div
          ref={triggerRef}
          role="presentation"
          aria-hidden
          style={chipStyle}
          className={chipSurfaceClass}
        >
          {accentBlock}
          {labelBlock}
        </div>
      )}
      {!hoverDisabled && debouncedVisible && hoverStyle ? (
        <EventHoverPortal event={event} style={hoverStyle} formatMode={timeFormatPreference} />
      ) : null}
    </>
  );
};

export default CalendarEventItem;
